import { ButtonPrimary } from 'components/Button'
import { EmptyState } from 'components/EmptyState'
import { MainContentWrapper, PositionListFooter } from 'components/PositionList/styled'
import V2PositionList from 'components/PositionList/v2'
import { RefreshControl } from 'pages/Liquidity'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { StyledInternalLink } from 'theme'

import { useUserV2Positions, V2_POSITIONS_PER_PAGE } from './hooks/useUserV2Positions'

export const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  padding: 6px 16px;
  border: none;
  width: fit-content;

  background-color: transparent;

  border-radius: 4px;
  border: 1px solid #64748b;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex: 1 1 auto;
    width: 100%;
  `};

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  color: ${({ theme }) => theme.textPrimary};

  &:hover {
    background: ${({ theme }) => theme.stateOverlayHover};
  }
`
const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  padding: 24px 0;
  max-width: 300px;
  /* min-height: 25vh; */
`

const Wrapper = styled.div`
  width: 100%;
`

export default function V2Pools({
  account,
  toggleWalletModal,
  searchParam,
  refreshControl,
}: {
  account?: string | null
  toggleWalletModal?: () => void
  searchParam?: string | null
  refreshControl?: RefreshControl
}) {
  const theme = useContext(ThemeContext)
  const [hasSettledOnce, setHasSettledOnce] = useState(false)

  const {
    loading: positionsLoading,
    positions,
    totalCount,
    currentPage,
    totalPages,
    goToPage,
    refetch,
    isSearching,
  } = useUserV2Positions(
    account,
    {
      pageSize: V2_POSITIONS_PER_PAGE,
      enablePagination: true,
    },
    searchParam || undefined
  )

  const prevLoading = useRef(positionsLoading)

  useEffect(() => {
    if (refreshControl?.onRegister && refetch) {
      const asyncRefetch = async () => {
        refetch()
      }
      refreshControl.onRegister(asyncRefetch)
    }

    return () => {
      if (refreshControl?.onRegister) {
        refreshControl.onRegister(null)
      }
    }
  }, [refreshControl?.onRegister, refetch])

  useEffect(() => {
    if (prevLoading.current && !positionsLoading) {
      setHasSettledOnce(true)
    }
    prevLoading.current = positionsLoading
  }, [positionsLoading])

  const hasData = positions.length > 0
  const showConnectAWallet = Boolean(!account)
  const isConnected = Boolean(account)

  const showTransitionOverlay = refreshControl?.isRefreshing || isSearching || (positionsLoading && !hasSettledOnce)
  const showList = isConnected && (positionsLoading || hasData) && !showTransitionOverlay
  const showEmptyShell = (!isConnected || (!positionsLoading && !hasData)) && !showTransitionOverlay

  const handlePageChange = useCallback(
    (page: number) => {
      goToPage(page - 1)
    },
    [goToPage]
  )

  const getTransitionLabel = () => {
    if (refreshControl?.isRefreshing) {
      return 'Refreshing V2 positions...'
    }
    if (isSearching) {
      if (searchParam) {
        const isAddressLike = /^0x[a-fA-F0-9]{40}$/.test(searchParam.trim())
        if (isAddressLike) {
          return `Searching for V2 token "${searchParam.slice(0, 6)}...${searchParam.slice(-4)}"...`
        }
        return `Searching V2 positions for "${searchParam}"...`
      }
      return 'Searching V2 positions...'
    }
    if (positionsLoading && !hasSettledOnce) {
      return 'Loading V2 positions...'
    }
    return 'Loading...'
  }

  return (
    <>
      <Wrapper>
        {(showList || showTransitionOverlay) && (
          <V2PositionList
            positions={positions}
            account={account}
            loading={false}
            totalCount={totalCount}
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            searchParam={searchParam}
            isSearching={false}
            showTransitionOverlay={showTransitionOverlay}
            transitionLabel={getTransitionLabel()}
          />
        )}

        {showEmptyShell && (
          <MainContentWrapper>
            <ErrorContainer>
              <EmptyState
                message={
                  showConnectAWallet
                    ? 'Connect your wallet to see your V2 positions.'
                    : searchParam && searchParam.length === 42
                    ? `No V2 positions found for token ${searchParam.slice(0, 6)}...${searchParam.slice(-4)}`
                    : 'Your active V2 liquidity positions will appear here.'
                }
                showConnectButton={showConnectAWallet}
              />
            </ErrorContainer>

            <PositionListFooter>
              <ResponsiveButtonPrimary
                id="find-pool-button"
                as={StyledInternalLink}
                to="/liquidity/v2/find"
                padding="6px 8px"
              >
                <>Import Pool</>
              </ResponsiveButtonPrimary>

              <ResponsiveButtonPrimary
                id="join-pool-button"
                as={StyledInternalLink}
                to="/liquidity/v2/add/BERA"
                padding="6px 8px"
                data-cy="join-pool-button"
              >
                <>Add V2 Liquidity</>
              </ResponsiveButtonPrimary>
            </PositionListFooter>
          </MainContentWrapper>
        )}
      </Wrapper>
    </>
  )
}
